<template>
	<!-- ダイアログ・スナックバーのみ。ボタンは ToolPanel.vue 側に直接配置 -->

	<!-- 種別選択ダイアログ -->
	<simple-structure-selection-dialog v-model="showSelectionDialog" @select="onStructureSelected"
		@select-manual="onManualSelected" @cancel="onSelectionCancel" />

	<!-- パラメータ入力ダイアログ -->
	<property-dialog-simple-structure v-if="showPropertyDialog" v-model="showPropertyDialog"
		:selectedStructureType="selectedStructureType" :initialPosition="pickedPosition"
		:initialTwoPoints="pickedTwoPointPositions" :tempMeshId="tempMeshId" @confirm="onParamsConfirm"
		@cancel="onParamsCancel" />
</template>

<script setup lang="ts">
import type { TSimpleStructureType } from "@/models/objects/solidMesh/types";
import { notifyError, notifySuccess } from "@/utils/notificationUtils";
import { watch } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";
import PropertyDialogSimpleStructure from "./PropertyDialogSimpleStructure.vue";
import SimpleStructureSelectionDialog from "./SimpleStructureSelectionDialog.vue";
import {
	type TSimpleStructureConfirmData,
	useSimpleStructureCreation,
} from "./composables/useSimpleStructureCreation";
import { useSimpleStructureCreatorUi } from "./composables/useSimpleStructureCreatorUi";

const store = useStore();
const { t } = useI18n();
const creation = useSimpleStructureCreation(store);
const guidanceSnackbarIds = new Set<string>();

const {
	showSelectionDialog,
	showPropertyDialog,
	twoPointPickingStep,
	pickedPosition,
	pickedTwoPointPositions,
	selectedStructureType,
	tempMeshId,
	basisPointKey1,
	basisPointKey2,
} = useSimpleStructureCreatorUi(store);

// Display success/error toasts when the store signals a notification.
watch(creation.creationNotification, (val) => {
	if (!val) return;
	const label = t(val.key);
	if (val.type === "success") {
		notifySuccess({ $store: store }, `${label} ${t("successful")}`);
	} else {
		notifyError({ $store: store }, `${label} ${t("failed")}`);
	}
	creation.clearCreationNotification();
});

watch(twoPointPickingStep, (step, prevStep) => {
	if (guidanceSnackbarIds.size > 0) {
		store.commit("hide_snackbars", Array.from(guidanceSnackbarIds));
	}
	guidanceSnackbarIds.clear();
	store.commit("clear_snackbars");

	if (step === 1) {
		notifySuccess(
			{ $store: store },
			t("TWO_POINT_PICK_STEP1", { point: t(basisPointKey1.value) }),
			-1,
		);
		const latestSnackbar = store.state.snackbars.at(-1);
		if (latestSnackbar?.id) {
			guidanceSnackbarIds.add(latestSnackbar.id);
		}
	} else if (step === 2) {
		notifySuccess(
			{ $store: store },
			t("TWO_POINT_PICK_STEP2", { point: t(basisPointKey2.value) }),
			-1,
		);
		const latestSnackbar = store.state.snackbars.at(-1);
		if (latestSnackbar?.id) {
			guidanceSnackbarIds.add(latestSnackbar.id);
		}
	}
});

const selectableStructureTypes = [
	"box-culvert",
	"retaining-wall",
	"manhole",
	"pipe",
	"manual",
] as const satisfies ReadonlyArray<TSimpleStructureType>;

function isSimpleStructureType(value: unknown): value is TSimpleStructureType {
	return (
		typeof value === "string" &&
		(selectableStructureTypes as readonly string[]).includes(value)
	);
}

function onStructureSelected(structureType: TSimpleStructureType) {
	if (isSimpleStructureType(structureType)) {
		creation.selectSimpleStructureType(structureType);
		return;
	}
	console.error("Invalid structure type:", structureType);
}

function onManualSelected() {
	creation.startManualSolidMeshCreation();
}

function onSelectionCancel() {
	creation.cancelSimpleStructureCreation();
}

function onParamsConfirm(data: unknown) {
	if (!data || typeof data !== "object" || !("structureType" in data)) {
		console.error("Invalid confirm data:", data);
		return;
	}
	creation.confirmSimpleStructureParams(data as TSimpleStructureConfirmData);
}

function onParamsCancel() {
	creation.cancelSimpleStructureCreation();
}
</script>
